import { formatDate, formatDateTime } from '@/lib/date';
import type { ConsentExpiryInfo, ConsentTimeline } from '@/types/candidates';
import { CONSENT_ENDING_SOON_DAYS } from '@hygge/shared';

/**
 * How the candidate list and the GDPR tab word and colour a consent's end day
 * [HW-1023]. The backend decides the state and the days left (UTC days, the
 * same rules as the nightly expiry job); nothing here reads the viewer's clock.
 */

/** Label of the GDPR filter option that keeps consents ending soon or expired. */
export const CONSENT_ENDING_FILTER_LABEL = 'Ending soon / expired';

/** The filter option's tooltip-length description. */
export const CONSENT_ENDING_FILTER_HINT = `Expired, or approved and ending within ${CONSENT_ENDING_SOON_DAYS} days`;

/** Short line under the GDPR badge: when the consent ends or ended. */
export function consentExpiryText(info: ConsentExpiryInfo): string {
  if (info.endsOn === null) return 'Expired (no end date on record)';
  const date = formatDate(info.endsOn, 'short');
  if (info.state === 'EXPIRED') return `Ended ${date}`;
  if (info.state === 'ENDING_SOON') {
    return info.daysLeft === 1
      ? `Ends tomorrow, ${date}`
      : `Ends in ${info.daysLeft} days, ${date}`;
  }
  return `Ends ${date}`;
}

/** Text colour of that line: amber when ending soon, red once ended. */
export function consentExpiryTone(info: ConsentExpiryInfo): string {
  switch (info.state) {
    case 'EXPIRED':
      return 'text-destructive';
    case 'ENDING_SOON':
      return 'text-amber-700 dark:text-amber-400';
    default:
      return 'text-muted-foreground';
  }
}

/** Row tint in the candidate list, so ending and ended consents stand out. */
export function consentExpiryRowTone(info: ConsentExpiryInfo | null): string {
  if (info?.state === 'EXPIRED') return 'bg-destructive/10';
  if (info?.state === 'ENDING_SOON') return 'bg-amber-500/20';
  return '';
}

/**
 * A consent event's time on the GDPR tab, in UTC and marked so: the same day
 * "Granted on" and the end date show, whatever the viewer's timezone.
 */
export function consentEventTimeUtc(createdAt: string): string {
  return `${formatDateTime(createdAt, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'UTC',
  })} UTC`;
}

/**
 * When a renewal email went out that the candidate has not answered yet: the
 * consent is ending soon or has expired, and the latest request came after the
 * latest grant. Null otherwise.
 */
export function renewalRequestedAt(
  timeline: Pick<ConsentTimeline, 'grantedAt' | 'lastRequestedAt' | 'consentExpiry'>
): string | null {
  const { grantedAt, lastRequestedAt, consentExpiry } = timeline;
  if (!grantedAt || !lastRequestedAt || !consentExpiry) return null;
  if (consentExpiry.state === 'ACTIVE') return null;
  return new Date(lastRequestedAt) > new Date(grantedAt) ? lastRequestedAt : null;
}
